import { IconClose } from '@belong/icons';
import { ButtonBase, Text } from '@belong/ui';
import cx from 'classnames';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import { DropdownFinalFormAdapter } from 'components/Dropdown/Dropdown';
import Field from 'components/Field/Field';
import { FormContext } from 'components/Form/Form';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  maskPhoneNumber,
  maskRemoveWhiteSpace,
  unmaskPhoneNumber,
  unmaskRemoveWhiteSpace,
} from 'components/Input/masks';
import {
  SelectorFinalFormAdapter,
  SELECTOR_TYPES,
} from 'components/Selector/Selector';
import { useField, useForm } from 'react-final-form';
import { STRINGS } from './strings';
import { formatProTrades } from './utils';
import type { ExecutionChoice, ReusablePro, SelectorState } from './types';

type Props = {
  state: SelectorState;
  reusablePros: ReusablePro[];
  showErrors: boolean;
};

type WarningProps = {
  className?: string;
};

const BUTTONS: { label: string; key: ExecutionChoice }[] = [
  { label: STRINGS.buttons.belongPros, key: 'BelongPro' },
  { label: STRINGS.buttons.myPro, key: 'HomeownerPro' },
];

export function ExecutionModeSelector({
  state,
  reusablePros,
  showErrors,
}: Props) {
  const form = useForm();

  if (!state.showSelector) return null;

  return (
    <>
      <Field
        name="executionChoice"
        className="mt-xl"
        component={SelectorFinalFormAdapter}
        type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
        fluid
        buttons={BUTTONS}
        disabled={state.buttonsDisabled}
        withMarginTop={false}
        onChangeCustom={() => form.change('manageProMyself', false)}
      />
      <div className="mt-sm flex w-full justify-end">
        <Field
          alignStart
          withMarginTop={false}
          useOnChangeFormToHandle
          name="manageProMyself"
          component={CheckboxFinalFormAdapter}
          label={STRINGS.manageMyself}
          alignWithField={false}
          disabled={state.manageMyselfDisabled}
          onClickCustom={(checked: boolean) => {
            if (checked) form.change('executionChoice', undefined);
          }}
        />
      </div>
      {state.showWarning && <HomeownerFixingWarning className="mt-2xl" />}
      {state.showProPicker && (
        <ProPicker reusablePros={reusablePros} showErrors={showErrors} />
      )}
      {state.showProForm && (
        <ProForm reusablePros={reusablePros} showErrors={showErrors} />
      )}
    </>
  );
}

function ProPicker({
  reusablePros,
  showErrors,
}: {
  reusablePros: ReusablePro[];
  showErrors: boolean;
}) {
  const { meta } = useField('homeownerProChoice', {
    subscription: { error: true },
  });

  const items = [
    ...reusablePros.map((pro) => ({
      key: String(pro.partnerId),
      value: pro.name,
      content: (
        <div>
          <Text variant="body">{pro.name}</Text>
          <Text variant="p1" className="text-dark-gray">
            {[
              formatProTrades(pro.trades),
              pro.phone && maskPhoneNumber(pro.phone),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </div>
      ),
    })),
    {
      key: 'new',
      value: STRINGS.picker.addNew,
      content: (
        <Text variant="body" fontWeight="semibold">
          {STRINGS.picker.addNew}
        </Text>
      ),
    },
  ];

  return (
    <div className="mt-2xl w-full">
      <Text variant="h3" fontWeight="semibold">
        {STRINGS.picker.title}
      </Text>
      <Text variant="p1" className="mt-xs text-dark-gray">
        {STRINGS.picker.subtitle}
      </Text>
      <FormContext.Provider value={showErrors}>
        <div className="mt-xl">
          <Field
            name="homeownerProChoice"
            component={DropdownFinalFormAdapter}
            label={STRINGS.picker.label}
            placeholder={STRINGS.picker.label}
            items={items}
            withMarginTop={false}
          />
          {showErrors && meta.error && (
            <Text variant="p1" className="mt-2xs text-red">
              {meta.error}
            </Text>
          )}
        </div>
      </FormContext.Provider>
    </div>
  );
}

function ProForm({
  reusablePros,
  showErrors,
}: {
  reusablePros: ReusablePro[];
  showErrors: boolean;
}) {
  const form = useForm();
  const firstPro = reusablePros[0];

  return (
    <div className="mt-2xl w-full">
      <div className="flex items-start justify-between gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {firstPro ? STRINGS.form.titleFromPicker : STRINGS.form.title}
        </Text>
        {firstPro && (
          <ButtonBase
            type="button"
            aria-label={STRINGS.form.backToPicker}
            onClick={() =>
              form.change('homeownerProChoice', String(firstPro.partnerId))
            }
          >
            <IconClose width={16} height={16} />
          </ButtonBase>
        )}
      </div>
      <Text variant="p1" className="mt-xs text-dark-gray">
        {STRINGS.form.subtitle}
      </Text>
      <FormContext.Provider value={showErrors}>
        <div className="mt-xl grid grid-cols-1 gap-x-sm gap-y-xl md:grid-cols-2">
          <Field
            name="homeownerPro.firstName"
            component={InputFinalFormAdapter}
            placeholder={STRINGS.form.firstName}
            withMarginTop={false}
          />
          <Field
            name="homeownerPro.lastName"
            component={InputFinalFormAdapter}
            placeholder={STRINGS.form.lastName}
            withMarginTop={false}
          />
        </div>
        <div className="mt-xl">
          <Field
            name="homeownerPro.company"
            component={InputFinalFormAdapter}
            placeholder={STRINGS.form.company}
            withMarginTop={false}
          />
        </div>
        <div className="mt-xl flex flex-col items-center gap-xl md:flex-row md:items-start">
          <div className="w-full">
            <Field
              name="homeownerPro.phone"
              component={InputFinalFormAdapter}
              placeholder={STRINGS.form.phone}
              mask={maskPhoneNumber}
              unmask={unmaskPhoneNumber}
              type="tel"
              withMarginTop={false}
            />
          </div>
          <Text
            variant="p1"
            fontWeight="semibold"
            className="text-dark-gray md:mt-[3.6rem] md:leading-[2.4rem]"
          >
            {STRINGS.form.or}
          </Text>
          <div className="w-full">
            <Field
              name="homeownerPro.email"
              component={InputFinalFormAdapter}
              placeholder={STRINGS.form.email}
              mask={maskRemoveWhiteSpace}
              unmask={unmaskRemoveWhiteSpace}
              type="email"
              withMarginTop={false}
            />
          </div>
        </div>
      </FormContext.Provider>
    </div>
  );
}

export function HomeownerFixingWarning({ className = 'mt-lg' }: WarningProps) {
  return (
    <div
      className={cx(
        className,
        'rounded border border-solid border-red bg-red-translucent-light bg-opacity-70 p-sm',
      )}
    >
      <p className="font-semibold leading-p1">{STRINGS.warning.title}</p>
      <p className="leading-p1">{STRINGS.warning.exit}</p>
      <br />
      <p className="leading-p1">{STRINGS.warning.consequences}</p>
      <br />
      <p className="leading-p1">{STRINGS.warning.alternative}</p>
    </div>
  );
}
